import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTimeOff } from '@/stores/timeOff';
import { useStaff } from '@/stores/staff';
import { useRole } from '@/stores/identity';
import {
  TIME_OFF_STATUS_LABELS,
  TIME_OFF_TYPES,
  formatTimeOffRange,
  timeOffDayCount,
} from '@/lib/timeOff';
import type { TimeOffStatus } from '@/lib/types';
import { humanize } from '@/lib/errors';

/**
 * Time-off directory.
 *
 * For staff: shows their own requests + a "+ Request time off" button.
 * For owners/managers: shows all requests they can see (RLS-scoped),
 *   pending first so the queue is obvious. Each row links to the detail
 *   page where they can approve / deny / cancel.
 */
export default function TimeOffList() {
  const status = useTimeOff((s) => s.status);
  const items = useTimeOff((s) => s.items);
  const error = useTimeOff((s) => s.error);
  const load = useTimeOff((s) => s.load);

  const staffStatus = useStaff((s) => s.status);
  const staffItems = useStaff((s) => s.items);
  const loadStaff = useStaff((s) => s.load);

  const role = useRole();
  const isAdmin = role === 'owner' || role === 'manager';

  useEffect(() => {
    if (status === 'idle') void load();
    if (staffStatus === 'idle') void loadStaff();
  }, [status, staffStatus, load, loadStaff]);

  const sorted = useMemo(() => {
    const order: Record<TimeOffStatus, number> = {
      pending: 0,
      approved: 1,
      denied: 2,
      withdrawn: 3,
      cancelled: 4,
    };
    return [...items].sort((a, b) => {
      const s = order[a.status] - order[b.status];
      if (s !== 0) return s;
      return b.start_date.localeCompare(a.start_date);
    });
  }, [items]);

  const staffById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of staffItems) m.set(s.id, s.display_name);
    return m;
  }, [staffItems]);

  return (
    <>
    <div className="container mx-auto py-8 px-4 print:hidden">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Time off</h1>
            <p className="text-sm text-muted-foreground">
              {isAdmin
                ? 'All time-off requests visible to you. Pending ones appear first — open a row to approve or deny.'
                : 'Your time-off requests. Click "+ Request time off" to submit a new one.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {status === 'loaded' && items.length > 0 && (
              <button
                type="button"
                onClick={() => window.print()}
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="Print time-off list"
              >
                Print
              </button>
            )}
            <Link
              to="/time-off/new"
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              + Request time off
            </Link>
          </div>
        </div>

        {status === 'loading' && (
          <p className="text-sm text-muted-foreground">Loading requests…</p>
        )}
        {status === 'error' && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {humanize(error)}
          </p>
        )}
        {status === 'loaded' && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No time-off requests yet.
          </div>
        )}

        {status === 'loaded' && items.length > 0 && (
          <ul className="space-y-2">
            {sorted.map((req) => {
              const typeLabel =
                TIME_OFF_TYPES.find((t) => t.value === req.type)?.label ??
                req.type;
              const days = timeOffDayCount(req);
              const staffName =
                staffById.get(req.staff_id) ?? '(unknown staff)';
              return (
                <li
                  key={req.id}
                  className="rounded-lg border border-border bg-card hover:bg-muted/30"
                >
                  <Link
                    to={`/time-off/${req.id}`}
                    className="flex items-center justify-between gap-4 p-4"
                  >
                    <div className="space-y-0.5 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-medium truncate">
                          {staffName}
                        </h3>
                        <StatusPill status={req.status} />
                        <span className="text-xs text-muted-foreground">
                          {typeLabel}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {formatTimeOffRange(req)} · {days}{' '}
                        {days === 1 ? 'day' : 'days'}
                      </p>
                    </div>
                    {req.reason && (
                      <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[14rem]">
                        “{req.reason}”
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>

    {/*
     * Print-only view. AppShell's <header>/<nav> are already hidden by the
     * global @media print rules in index.css; we render a clean brand
     * header + tabular list. Same sort order as the screen list so what
     * the owner saw on screen is what prints. Falls back to "no rows" gracefully.
     */}
    {status === 'loaded' && items.length > 0 && (
      <div className="hidden print:block">
        <header className="border-b-2 border-[#1A2D5C] pb-2 mb-4 flex items-baseline justify-between">
          <div className="flex items-baseline gap-3">
            <span className="text-[18pt] font-semibold text-[#1A2D5C]">PharmaSync</span>
            <span className="text-[11pt] text-[#5F5E5A]">Time-off requests</span>
          </div>
          <span className="text-[9pt] text-[#5F5E5A]">
            Generated {new Date().toLocaleDateString('en-CA', { dateStyle: 'long' })} · {sorted.length} request{sorted.length === 1 ? '' : 's'}
          </span>
        </header>
        <table className="w-full text-[10pt] border-collapse">
          <thead>
            <tr className="border-b border-[#1A2D5C] text-left">
              <th className="py-1 pr-3 font-semibold">Staff</th>
              <th className="py-1 pr-3 font-semibold">Type</th>
              <th className="py-1 pr-3 font-semibold">Dates</th>
              <th className="py-1 pr-3 font-semibold text-right">Days</th>
              <th className="py-1 pr-3 font-semibold">Status</th>
              <th className="py-1 font-semibold">Reason</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((req) => {
              const typeLabel =
                TIME_OFF_TYPES.find((t) => t.value === req.type)?.label ?? req.type;
              const days = timeOffDayCount(req);
              const staffName = staffById.get(req.staff_id) ?? '(unknown staff)';
              return (
                <tr key={req.id} className="border-b border-[#E6E5DF]">
                  <td className="py-1 pr-3 align-top">{staffName}</td>
                  <td className="py-1 pr-3 align-top">{typeLabel}</td>
                  <td className="py-1 pr-3 align-top whitespace-nowrap">{formatTimeOffRange(req)}</td>
                  <td className="py-1 pr-3 align-top text-right tabular-nums">{days}</td>
                  <td className="py-1 pr-3 align-top capitalize">{TIME_OFF_STATUS_LABELS[req.status]}</td>
                  <td className="py-1 align-top">{req.reason ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <footer className="mt-3 text-[8pt] text-[#888780] flex items-center justify-between">
          <span>Powered by PharmaSync · pharma-sync.ca</span>
          <span>Blockchain PMO Inc. · Toronto, ON, Canada</span>
        </footer>
      </div>
    )}
    </>
  );
}

function StatusPill({ status }: { status: TimeOffStatus }) {
  const styles: Record<TimeOffStatus, string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    denied: 'bg-red-100 text-red-800',
    withdrawn: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-gray-100 text-gray-700',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}
    >
      {TIME_OFF_STATUS_LABELS[status]}
    </span>
  );
}
